import { useCallback, useEffect, useRef, useState } from 'react';
import { getFileType } from '../utils/photoHelpers';
import api, { guestApiPath } from '../../../api';
import { getGuestAuthToken } from '../../../utils/guestToken';

const NEARBY_SLIDE_RADIUS = 1;
const MAX_VIEW_URLS_PER_REQUEST = 100;
const REFRESH_INTERVAL_MS = 60 * 1000;
const EXPIRY_SAFETY_MARGIN_MS = 30 * 1000;
const TERMINAL_VIEW_URL_ERRORS = new Set([
  'File not found',
  'Invalid path for this gallery',
  'Preview not available',
]);

function isEntryFresh(entry, now) {
  return Boolean(entry) && typeof entry.expiresAt === 'number'
    && entry.expiresAt - EXPIRY_SAFETY_MARGIN_MS > now;
}

function mergeViewEntries(prev, responseUrls) {
  let changed = false;
  const next = new Map(prev);
  (responseUrls || []).forEach((entry) => {
    if (!entry?.fileName) return;
    if (entry.url || entry.thumbnailUrl) {
      const existing = next.get(entry.fileName) || {};
      const merged = {
        url: entry.url || existing.url || null,
        thumbnailUrl: entry.thumbnailUrl || existing.thumbnailUrl || null,
        expiresAt: entry.expiresAt || existing.expiresAt || null,
      };
      const current = next.get(entry.fileName);
      if (!current || current.url !== merged.url
        || current.thumbnailUrl !== merged.thumbnailUrl
        || current.expiresAt !== merged.expiresAt) {
        next.set(entry.fileName, merged);
        changed = true;
      }
    }
  });
  return changed ? next : prev;
}

function isTerminalViewUrlFailure(entry) {
  return Boolean(entry)
    && !entry.url
    && !entry.thumbnailUrl
    && entry.retryable !== true
    && (entry.retryable === false || TERMINAL_VIEW_URL_ERRORS.has(entry.error));
}

/**
 * Lazily fetches signed URLs for gallery slides.
 *
 * Full-size view URLs are requested for image slides near the active index,
 * while signed thumbnail URLs are fetched for every photo (images and videos)
 * so posters and distant-slide placeholders never hit the private bucket
 * unsigned. Signed URLs are short-lived, so entries track their `expiresAt`
 * and are refreshed before reuse; only confirmed missing or unsupported files
 * are recorded as terminal so transient signing failures can recover.
 *
 * @param {Array<string>} photos - Download keys / filenames of all loaded slides
 * @param {number} activeIndex - Currently active slide index
 * @param {boolean} isActive - Whether the consumer is mounted/visible
 * @param {string} galleryId - Resolved gallery tenant id (explicit, not shared storage)
 * @returns {{ getViewUrl: (filename: string) => string | null,
 *             getThumbnailUrl: (filename: string) => string | null,
 *             hasViewUrlError: (filename: string) => boolean,
 *             refreshViewUrl: (filename: string) => Promise<string> }}
 */
export const useMediaViewUrls = (photos = [], activeIndex = 0, isActive = true, galleryId = '') => {
  const [viewEntries, setViewEntries] = useState(() => new Map());
  const [viewUrlFailures, setViewUrlFailures] = useState(() => new Set());
  const [refreshTick, setRefreshTick] = useState(0);
  const fetchingRef = useRef(new Set());
  const failedRef = useRef(new Set());
  const requestVersionRef = useRef(new Map());
  const latestContextRef = useRef({ galleryId, isActive });
  latestContextRef.current = { galleryId, isActive };

  const updateViewUrlFailures = useCallback((failedFilenames, recoveredFilenames = []) => {
    setViewUrlFailures((current) => {
      const next = new Set(current);
      recoveredFilenames.forEach((filename) => next.delete(filename));
      failedFilenames.forEach((filename) => next.add(filename));
      if (next.size === current.size && [...next].every((filename) => current.has(filename))) {
        return current;
      }
      return next;
    });
  }, []);

  useEffect(() => () => {
    latestContextRef.current = { ...latestContextRef.current, isActive: false };
  }, []);

  const refreshViewUrl = useCallback(async (filename) => {
    if (!filename || !isActive) {
      throw new Error('Photo viewer is not active');
    }

    const token = getGuestAuthToken();
    if (!token) {
      throw new Error('Missing gallery authentication');
    }

    const requestId = (requestVersionRef.current.get(filename) || 0) + 1;
    requestVersionRef.current.set(filename, requestId);
    failedRef.current.delete(filename);

    const response = await api.post(`${guestApiPath('/view-urls', galleryId)}`, { fileNames: [filename] }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const entry = (response.data?.urls || []).find((candidate) => candidate?.fileName === filename);
    const isCurrentRequest = requestVersionRef.current.get(filename) === requestId;
    const latestContext = latestContextRef.current;

    if (!isCurrentRequest || !latestContext.isActive || latestContext.galleryId !== galleryId) {
      throw new Error('Photo refresh is no longer active');
    }
    if (!entry?.url) {
      if (isTerminalViewUrlFailure(entry)) {
        failedRef.current.add(filename);
      }
      updateViewUrlFailures([filename]);
      throw new Error(entry?.error || 'Unable to refresh photo URL');
    }

    setViewEntries((prev) => mergeViewEntries(prev, [entry]));
    updateViewUrlFailures([], [filename]);
    return entry.url;
  }, [galleryId, isActive, updateViewUrlFailures]);

  // Periodically re-evaluate expiry while active so stale signed URLs are
  // refreshed before they are reused.
  useEffect(() => {
    if (!isActive) return undefined;
    const interval = setInterval(() => setRefreshTick((tick) => tick + 1), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isActive]);

  useEffect(() => {
    if (!isActive || !Array.isArray(photos) || photos.length === 0) return;

    const now = Date.now();
    const targets = [];
    const lower = Math.max(0, activeIndex - NEARBY_SLIDE_RADIUS);
    const upper = Math.min(photos.length - 1, activeIndex + NEARBY_SLIDE_RADIUS);
    for (let i = lower; i <= upper; i += 1) {
      const filename = photos[i];
      if (
        filename
        && getFileType(filename) !== 'video'
        && !failedRef.current.has(filename)
        && !fetchingRef.current.has(filename)
        && !isEntryFresh(viewEntries.get(filename), now)
      ) {
        targets.push(filename);
      }
    }
    if (targets.length === 0) return;

    const token = getGuestAuthToken();
    if (!token) return;

    const requestVersions = new Map();
    targets.forEach((filename) => {
      fetchingRef.current.add(filename);
      const version = (requestVersionRef.current.get(filename) || 0) + 1;
      requestVersionRef.current.set(filename, version);
      requestVersions.set(filename, version);
    });

    api.post(`${guestApiPath('/view-urls', galleryId)}`, { fileNames: targets }, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        const currentEntries = (response.data?.urls || []).filter((entry) => (
          entry?.fileName && requestVersionRef.current.get(entry.fileName) === requestVersions.get(entry.fileName)
        ));
        currentEntries.forEach((entry) => {
          if (!entry?.fileName) return;
          if (isTerminalViewUrlFailure(entry)) {
            failedRef.current.add(entry.fileName);
          }
        });
        setViewEntries((prev) => mergeViewEntries(prev, currentEntries));
        const recovered = currentEntries.filter((entry) => entry.url).map((entry) => entry.fileName);
        const failed = targets.filter((filename) => (
          requestVersionRef.current.get(filename) === requestVersions.get(filename)
          && !currentEntries.some((entry) => entry.fileName === filename && entry.url)
        ));
        updateViewUrlFailures(failed, recovered);
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          // Auth issue will be handled by the global interceptor in api.js
        } else {
          console.warn('Failed to fetch signed view URLs:', error.message || error);
          const failed = targets.filter((filename) => (
            requestVersionRef.current.get(filename) === requestVersions.get(filename)
          ));
          updateViewUrlFailures(failed);
        }
      })
      .finally(() => {
        targets.forEach((filename) => fetchingRef.current.delete(filename));
      });
  }, [isActive, photos, activeIndex, galleryId, viewEntries, refreshTick, updateViewUrlFailures]);

  // Fetch signed thumbnail URLs for every photo (including videos) in batches,
  // so posters and distant-slide placeholders are never unsigned private URLs.
  useEffect(() => {
    if (!isActive || !Array.isArray(photos) || photos.length === 0) return;

    const now = Date.now();
    const targets = [];
    for (const filename of photos) {
      if (targets.length >= MAX_VIEW_URLS_PER_REQUEST) break;
      if (!filename || failedRef.current.has(filename) || fetchingRef.current.has(filename)) {
        continue;
      }
      const entry = viewEntries.get(filename);
      if (entry?.thumbnailUrl && isEntryFresh(entry, now)) {
        continue;
      }
      targets.push(filename);
    }
    if (targets.length === 0) return;

    const token = getGuestAuthToken();
    if (!token) return;

    const requestVersions = new Map();
    targets.forEach((filename) => {
      fetchingRef.current.add(filename);
      const version = (requestVersionRef.current.get(filename) || 0) + 1;
      requestVersionRef.current.set(filename, version);
      requestVersions.set(filename, version);
    });

    api.post(`${guestApiPath('/view-urls', galleryId)}`, { fileNames: targets }, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => {
        const currentEntries = (response.data?.urls || []).filter((entry) => (
          entry?.fileName && requestVersionRef.current.get(entry.fileName) === requestVersions.get(entry.fileName)
        ));
        currentEntries.forEach((entry) => {
          if (!entry?.fileName) return;
          if (isTerminalViewUrlFailure(entry)) {
            failedRef.current.add(entry.fileName);
          }
        });
        setViewEntries((prev) => mergeViewEntries(prev, currentEntries));
        const imageTargets = targets.filter((filename) => getFileType(filename) !== 'video');
        const recovered = currentEntries.filter((entry) => entry.url).map((entry) => entry.fileName);
        const failed = imageTargets.filter((filename) => (
          requestVersionRef.current.get(filename) === requestVersions.get(filename)
          && !currentEntries.some((entry) => entry.fileName === filename && entry.url)
        ));
        updateViewUrlFailures(failed, recovered);
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          // Auth issue will be handled by the global interceptor in api.js
        } else {
          console.warn('Failed to fetch signed thumbnail URLs:', error.message || error);
          const failed = targets.filter((filename) => (
            getFileType(filename) !== 'video'
            && requestVersionRef.current.get(filename) === requestVersions.get(filename)
          ));
          updateViewUrlFailures(failed);
        }
      })
      .finally(() => {
        targets.forEach((filename) => fetchingRef.current.delete(filename));
      });
  }, [isActive, photos, galleryId, viewEntries, refreshTick, updateViewUrlFailures]);

  return {
    getViewUrl: (filename) => {
      if (!filename) return null;
      const entry = viewEntries.get(filename);
      return entry?.url && isEntryFresh(entry, Date.now()) ? entry.url : null;
    },
    getThumbnailUrl: (filename) => {
      if (!filename) return null;
      const entry = viewEntries.get(filename);
      return entry?.thumbnailUrl && isEntryFresh(entry, Date.now()) ? entry.thumbnailUrl : null;
    },
    hasViewUrlError: (filename) => Boolean(filename) && viewUrlFailures.has(filename),
    refreshViewUrl,
  };
};
